import { API_BASE_URL } from '../config';

const messages = {
  REQUEST_ACCOUNT_CHANGED: 'Account changed. Camera request cancelled.',
  CAMERA_INVALID_INPUT: 'Invalid camera request.',
  CAMERA_INVALID_RESPONSE: 'Invalid camera response.',
  CAMERA_REQUEST_TIMEOUT: 'Camera request timed out. Outcome unknown; retry stop.',
  CAMERA_REQUEST_FAILED: 'Camera request failed.',
};
const fail = code => Object.assign(new Error(messages[code]), { code });
const sessionId = value => typeof value === 'string' && /^[a-f0-9]{24}$/.test(value);
const operationKey = value => typeof value === 'string' && /^[A-Za-z0-9_-]{8,80}$/.test(value);
// eslint-disable-next-line no-control-regex
const deviceName = value => typeof value === 'string' && value.trim().length >= 1 && value.trim().length <= 60 && !/[\u0000-\u001f\u007f]/.test(value);
const liveDevice = value => value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).sort().join() === 'deviceId,deviceName'
  && typeof value.deviceId === 'string' && /^[A-Za-z0-9_-]{16,64}$/.test(value.deviceId) && deviceName(value.deviceName);
// Server label of a daily Live video: its local day and display-only device name.
const liveLabel = value => value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).sort().join() === 'day,deviceName'
  && /^\d{4}-\d{2}-\d{2}$/.test(value.day) && deviceName(value.deviceName);

// Exact v1 authority only. Never normalize a missing or contradictory field into
// acceptance. Shared with the controller so injected adapters cannot bypass it.
export function readCameraOperationOutcome(value, ownerId, key, knownSessionId) {
  const fields = ['version', 'ownerId', 'operationKey', 'sessionId', 'accepted', 'reason'];
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || Object.keys(value).length !== fields.length || fields.some(f => !Object.prototype.hasOwnProperty.call(value, f))
    || value.version !== 1 || !sessionId(ownerId) || value.ownerId !== ownerId
    || !operationKey(key) || value.operationKey !== key
    || (value.sessionId !== null && !sessionId(value.sessionId))
    || (knownSessionId != null && value.sessionId !== knownSessionId)
    || typeof value.accepted !== 'boolean'
    || !['absent', 'not-cancelled', 'unconfirmed', 'cancelled-before-start', 'provider-terminal'].includes(value.reason)
    || value.accepted !== ['cancelled-before-start', 'provider-terminal'].includes(value.reason)
    || (['absent', 'cancelled-before-start'].includes(value.reason) && value.sessionId !== null)
    || (value.reason === 'provider-terminal' && !sessionId(value.sessionId))) throw fail('CAMERA_INVALID_RESPONSE');
  return Object.freeze(Object.fromEntries(fields.map(f => [f, value[f]])));
}

// Validate every slot before normalization; Array.from exposes sparse entries.
export function canonicalCameraAudiences(values) {
  const allowed = ['friends', 'dojo', 'dojo_chief', 'public'];
  if (!Array.isArray(values) || Array.from(values).some(v => v !== 'private' && !allowed.includes(v))) throw fail('CAMERA_INVALID_INPUT');
  const unique = [...new Set(values)];
  if (unique.includes('private')) {
    if (unique.length !== 1) throw fail('CAMERA_INVALID_INPUT');
    return [];
  }
  return unique.includes('public') ? ['public'] : allowed.filter(v => unique.includes(v));
}

export function readPrivateTracerCapability(value) {
  if (value === undefined) return undefined;
  if (!value || Object.keys(value).sort().join(',') !== 'allowed,enabled,videoOnly'
    || value.enabled !== true || typeof value.allowed !== 'boolean' || value.videoOnly !== true) throw fail('CAMERA_INVALID_RESPONSE');
  return Object.freeze({ enabled: true, allowed: value.allowed, videoOnly: true });
}

// This adapter deliberately consumes the app service's existing Axios client and
// interceptor. It never acquires credentials itself or accepts caller auth config.
export function createSelfHostedCameraApi({ backendService, identity, ownerId, requestTimeoutMs = 10000 } = {}) {
  if (!backendService?.client || typeof identity !== 'string' || !identity || !sessionId(ownerId)
    || !Number.isFinite(requestTimeoutMs) || requestTimeoutMs < 1 || requestTimeoutMs > 30000) throw fail('CAMERA_INVALID_INPUT');
  let base;
  try {
    const url = new URL(API_BASE_URL || window.location.origin);
    if (url.username || url.password || url.search || url.hash || url.pathname !== '/'
      || !(url.protocol === 'https:' || (url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)))) throw fail('CAMERA_INVALID_INPUT');
    base = url.origin + '/api/camera';
  } catch { throw fail('CAMERA_INVALID_INPUT'); }
  const scope = Object.freeze({ identity, ownerId });
  const assertCurrent = () => {
    if (backendService.authenticatedIdentity !== identity || backendService.positionOwner !== ownerId) throw fail('REQUEST_ACCOUNT_CHANGED');
  };
  async function call(method, path, data, externalSignal) {
    assertCurrent();
    const abort = new AbortController();
    const cancel = () => abort.abort();
    externalSignal?.addEventListener('abort', cancel, { once: true });
    if (externalSignal?.aborted) abort.abort();
    let timer;
    const deadline = new Promise((_, reject) => {
      timer = setTimeout(() => { abort.abort(); reject(fail('CAMERA_REQUEST_TIMEOUT')); }, requestTimeoutMs);
    });
    try {
      const request = backendService.client.request({ method, url: base + path, data,
        timeout: requestTimeoutMs, signal: abort.signal, withCredentials: false,
        katafitEventIdentity: identity, katafitEventOwner: ownerId, katafitEventPosition: null });
      const response = await Promise.race([request, deadline]);
      assertCurrent();
      return response.data;
    } catch (error) {
      assertCurrent();
      throw fail(messages[error?.code] ? error.code : 'CAMERA_REQUEST_FAILED');
    } finally { clearTimeout(timer); externalSignal?.removeEventListener('abort', cancel); }
  }
  const checkedId = id => { if (!sessionId(id)) throw fail('CAMERA_INVALID_INPUT'); return id; };
  function readSession(body, expectedId, nullable = false) {
    if (nullable && body?.session === null) return { session: null };
    const s = body?.session;
    try {
      if (!s || !sessionId(s.id) || (expectedId && s.id !== expectedId) || s.ownerId !== ownerId
        || !['ready', 'live', 'ending', 'finalizing', 'ended', 'failed'].includes(s.state)
        || typeof s.audio_enabled !== 'boolean' || (s.privateTracer !== undefined && s.privateTracer !== true)
        || (s.live !== undefined && !liveLabel(s.live))) throw fail('CAMERA_INVALID_RESPONSE');
      return { session: { id: s.id, ownerId, state: s.state,
        audiences: canonicalCameraAudiences(s.audiences), audio_enabled: s.audio_enabled, ...(s.privateTracer ? { privateTracer: true } : {}),
        ...(s.live ? { live: { day: s.live.day, deviceName: s.live.deviceName.trim() } } : {}) } };
    } catch { throw fail('CAMERA_INVALID_RESPONSE'); }
  }
  function readSettings(body) {
    try {
      if (typeof body?.audio_enabled !== 'boolean') throw fail('CAMERA_INVALID_RESPONSE');
      const privateTracer = readPrivateTracerCapability(body.privateTracer);
      return { default_audiences: canonicalCameraAudiences(body.default_audiences), audio_enabled: body.audio_enabled, ...(privateTracer ? { privateTracer } : {}) };
    } catch { throw fail('CAMERA_INVALID_RESPONSE'); }
  }
  return Object.freeze({ scope, assertCurrent,
    ownerEligibility: async () => {
      const body = await call('get', '/owner-eligibility');
      if (!body || Object.keys(body).join(',') !== 'cameraEligibility' || body.cameraEligibility !== 'ready') throw fail('CAMERA_INVALID_RESPONSE');
      return body;
    },
    viewerToken: async id => {
      const grant = await call('post', `/sessions/${checkedId(id)}/viewer-token`);
      if (grant?.role !== 'read' || typeof grant.token !== 'string' || typeof grant.whepUrl !== 'string'
        || !(Date.parse(grant.expiresAt) > Date.now())) throw fail('CAMERA_INVALID_RESPONSE');
      return { role: grant.role, token: grant.token, whepUrl: grant.whepUrl, expiresAt: grant.expiresAt };
    },
    getSettings: async () => readSettings(await call('get', '/settings')),
    putSettings: async body => {
      if (typeof body?.audio_enabled !== 'boolean') throw fail('CAMERA_INVALID_INPUT');
      return readSettings(await call('put', '/settings', { default_audiences: canonicalCameraAudiences(body.default_audiences), audio_enabled: body.audio_enabled }));
    },
    eligibleMedia: async () => {
      const body = await call('get', '/eligible-media');
      if (!body || !Array.isArray(body.activities) || body.activities.length > 50 || typeof body.truncated !== 'boolean'
        || body.activities.some(a => !a || !sessionId(a.id) || typeof a.name !== 'string' || !a.name.trim() || a.name.length > 120)
        || new Set(body.activities.map(a => a.id)).size !== body.activities.length) throw fail('CAMERA_INVALID_RESPONSE');
      return { activities: body.activities.map(a => ({ id: a.id, name: a.name })), truncated: body.truncated };
    },
    start: async body => {
      if (!operationKey(body?.operationKey) || typeof body?.audio_enabled !== 'boolean') throw fail('CAMERA_INVALID_INPUT');
      let media;
      if (body.media !== undefined) {
        const value = body.media;
        if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length !== 1) throw fail('CAMERA_INVALID_INPUT');
        if (sessionId(value.activityId)) media = { activityId: value.activityId };
        else if (typeof value.name === 'string' && value.name.trim().length >= 1 && value.name.trim().length <= 120) media = { name: value.name.trim() };
        else throw fail('CAMERA_INVALID_INPUT');
      }
      // Daily Live is Private and exclusive with an explicit Media selection.
      if (body.live !== undefined && (media || !liveDevice(body.live) || canonicalCameraAudiences(body.audiences).length)) throw fail('CAMERA_INVALID_INPUT');
      const live = body.live && { deviceId: body.live.deviceId, deviceName: body.live.deviceName.trim() };
      const result = await call('post', '/sessions', { operationKey: body.operationKey,
        audiences: canonicalCameraAudiences(body.audiences), audio_enabled: body.audio_enabled, ...(media ? { media } : {}), ...(live ? { live } : {}) });
      const value = readSession(result);
      const p = result.publish;
      if (!p || typeof p.token !== 'string' || typeof p.whipUrl !== 'string' || typeof p.expiresAt !== 'string') throw fail('CAMERA_INVALID_RESPONSE');
      return { ...value, publish: { token: p.token, whipUrl: p.whipUrl, expiresAt: p.expiresAt } };
    },
    cancel: async key => {
      if (!operationKey(key)) throw fail('CAMERA_INVALID_INPUT');
      const body = await call('post', '/sessions/cancel', { operationKey: key });
      const result = readSession(body, null, true);
      const outcome = readCameraOperationOutcome(body?.outcome, ownerId, key);
      if (outcome.sessionId !== (result.session?.id || null)) throw fail('CAMERA_INVALID_RESPONSE');
      return { ...result, outcome };
    },
    operationOutcome: async key => {
      if (!operationKey(key)) throw fail('CAMERA_INVALID_INPUT');
      const body = await call('get', `/operations/${key}`);
      return { outcome: readCameraOperationOutcome(body?.outcome, ownerId, key) };
    },
    stop: async id => readSession(await call('post', `/sessions/${checkedId(id)}/stop`), id),
    status: async id => readSession(await call('get', `/sessions/${checkedId(id)}`), id),
    updateAudiences: async (id, audiences) => readSession(await call('patch', `/sessions/${checkedId(id)}`, { audiences: canonicalCameraAudiences(audiences) }), id),
  });
}
